import React from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const PricingEditor = ({ content, onContentChange, onMultipleContentChanges, site, onCreateCustomTheme }) => {
  const handlePlanChange = (index, field, value) => {
    const updatedPlans = [...(content.plans || [])];
    updatedPlans[index] = { ...updatedPlans[index], [field]: value };
    onContentChange('plans', updatedPlans);
  };

  const handleFeatureChange = (planIndex, featureIndex, value) => {
    const updatedPlans = [...(content.plans || [])];
    const updatedFeatures = [...(updatedPlans[planIndex].features || [])];
    updatedFeatures[featureIndex] = value;
    updatedPlans[planIndex] = { ...updatedPlans[planIndex], features: updatedFeatures };
    onContentChange('plans', updatedPlans);
  };

  const addPlan = () => {
    const newPlan = {
      name: '',
      price: '',
      period: 'per month',
      description: '',
      features: [''],
      popular: false,
      ctaText: 'Get Started'
    };
    onContentChange('plans', [...(content.plans || []), newPlan]);
  };

  const removePlan = (index) => {
    const updatedPlans = (content.plans || []).filter((_, i) => i !== index);
    onContentChange('plans', updatedPlans);
  };

  const addFeature = (planIndex) => {
    const updatedPlans = [...(content.plans || [])];
    updatedPlans[planIndex].features = [...(updatedPlans[planIndex].features || []), ''];
    onContentChange('plans', updatedPlans);
  };

  const removeFeature = (planIndex, featureIndex) => {
    const updatedPlans = [...(content.plans || [])];
    updatedPlans[planIndex].features = updatedPlans[planIndex].features.filter((_, i) => i !== featureIndex);
    onContentChange('plans', updatedPlans);
  };

  const togglePopular = (planIndex) => {
    const updatedPlans = [...(content.plans || [])];
    // First, set all plans to not popular
    updatedPlans.forEach(plan => plan.popular = false);
    // Then set the selected plan as popular
    updatedPlans[planIndex].popular = true;
    onContentChange('plans', updatedPlans);
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section title</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Our Pricing Plans"
        />
      </div>

      {/* Section Subtitle */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section subtitle</label>
        <textarea
          rows={2}
          value={content.subtitle || ''}
          onChange={(e) => onContentChange('subtitle', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="Transparent pricing that grows with your business"
        />
      </div>

      {/* Pricing Plans */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-900">Pricing plans</label>
          <button
            onClick={addPlan}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add plan
          </button>
        </div>

        <div className="space-y-3">
          {(content.plans || []).map((plan, planIndex) => (
            <div key={planIndex} className="border border-gray-200 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">Plan {planIndex + 1}</span>
                  {plan.popular && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">
                      <Star className="w-3 h-3" />
                      Popular
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removePlan(planIndex)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>

              {/* Plan Name */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Plan name</label>
                <input
                  type="text"
                  value={plan.name || ''}
                  onChange={(e) => handlePlanChange(planIndex, 'name', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Basic Plan"
                />
              </div>

              {/* Price and Period */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-900 mb-1">Price</label>
                  <input
                    type="text"
                    value={plan.price || ''}
                    onChange={(e) => handlePlanChange(planIndex, 'price', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="$99"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-900 mb-1">Period</label>
                  <select
                    value={plan.period || 'per month'}
                    onChange={(e) => handlePlanChange(planIndex, 'period', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="per month">per month</option>
                    <option value="per year">per year</option>
                    <option value="one-time">one-time</option>
                    <option value="per week">per week</option>
                    <option value="per day">per day</option>
                  </select>
                </div>
              </div>

              {/* Plan Description */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={plan.description || ''}
                  onChange={(e) => handlePlanChange(planIndex, 'description', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Brief description of what's included"
                />
              </div>

              {/* CTA Text */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Button text</label>
                <input
                  type="text"
                  value={plan.ctaText || ''}
                  onChange={(e) => handlePlanChange(planIndex, 'ctaText', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Get Started"
                />
              </div>

              {/* Popular Toggle */}
              <div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={plan.popular || false}
                    onChange={() => togglePopular(planIndex)}
                    className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                  />
                  Mark as popular plan
                </label>
              </div>

              {/* Plan Features */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-900">Features included</label>
                  <button
                    onClick={() => addFeature(planIndex)}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {(plan.features || []).map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(planIndex, featureIndex, e.target.value)}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Feature description"
                      />
                      <button
                        onClick={() => removeFeature(planIndex, featureIndex)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {(content.plans || []).length === 0 && (
          <div className="text-center py-4 border border-dashed border-gray-300 rounded">
            <p className="text-xs text-gray-500 mb-2">No pricing plans added yet</p>
            <button
              onClick={addPlan}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"
            >
              <Plus className="w-3 h-3" />
              Add your first pricing plan
            </button>
          </div>
        )}
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default PricingEditor;